import './Admin.scss';
import { format, isMonday, parseISO, subDays, subMonths } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { DatePicker } from '@Components/DatePicker';
import { Intent } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { useSetError } from '@src/redux/modals';
import { DATE_FORMAT } from '@src/utils/data';
import { CURRENT_WEEK_START } from '@src/utils/date-utils';
import { useQueryClient } from '@tanstack/react-query';
import { AdminAction, AdminActionType, getPotentiallyMissingHeaders } from './admin-constants';
import {
  downloadAllTables,
  filterLogSummariesByDateRange,
  LogSummariesWithConfirmedDNRs
} from './admin-utils';
import { AdminActionButtons } from './AdminActionButtons';
import { AdminTables } from './AdminTables';
import {
  ADMIN_LOG_SUMMARIES_QUERY_KEY,
  clearCompaniesAndUsersCache,
  clearSheetsCache,
  downloadMissingLinearTapesReport,
  LogSummaries,
  LogSummaryRow,
  updateAdServerTraffic,
  updateLinearReconciliation,
  useLogSummaries
} from './use-admin-data';

export const CUTOFF_DATE_PAST = format(subMonths(parseISO(CURRENT_WEEK_START), 6), DATE_FORMAT);
export const CUTOFF_DATE_FUTURE = format(subDays(parseISO(CURRENT_WEEK_START), 7), DATE_FORMAT);

const PAGE_TITLE = 'Admin Functions';

export const Admin = () => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  const { data: fetchedLogSummaries, isLoading, error } = useLogSummaries();
  const [start, setStart] = useState(CUTOFF_DATE_PAST);
  const [end, setEnd] = useState(CUTOFF_DATE_FUTURE);
  const [showUpdateTraffic, setShowUpdateTraffic] = useState(false);
  const [workingAction, setWorkingAction] = useState<AdminActionType | null>(null);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load data",
        message: `Failed to load log summaries: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const confirmedDNRs = useMemo(() => {
    if (!fetchedLogSummaries) {
      return [];
    }
    return fetchedLogSummaries.linearReconciliationStatus
      .filter(row => row.status === 'DNR')
      .map(row => ({
        week: row.week,
        company: row.company,
        network: row.network,
        avail: row.avail
      }));
  }, [fetchedLogSummaries]);

  const filteredLogSummaries = useMemo(() => {
    if (!fetchedLogSummaries) {
      return undefined;
    }
    const withConfirmedDNRs: LogSummariesWithConfirmedDNRs = {
      ...fetchedLogSummaries,
      confirmedDNRs
    };
    return filterLogSummariesByDateRange(withConfirmedDNRs, start, end);
  }, [start, end, fetchedLogSummaries, confirmedDNRs]);

  const updateTraffic = useCallback(async () => {
    setWorkingAction(AdminAction.UPDATE_TRAFFIC);
    try {
      await updateAdServerTraffic();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setWorkingAction(null);
  }, [setError]);

  const clearSheets = useCallback(async () => {
    setWorkingAction(AdminAction.CLEAR_SHEETS);
    try {
      await clearSheetsCache();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setWorkingAction(null);
  }, [setError]);

  const downloadLinearTapesReport = useCallback(async () => {
    setWorkingAction(AdminAction.DOWNLOAD_TAPES);
    try {
      await downloadMissingLinearTapesReport();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setWorkingAction(null);
  }, [setError]);

  const clearCompaniesAndUsers = useCallback(async () => {
    setWorkingAction(AdminAction.CLEAR_COMPANIES);
    try {
      await clearCompaniesAndUsersCache();
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setWorkingAction(null);
  }, [setError]);

  const downloadAll = useCallback(() => {
    if (!filteredLogSummaries) return;
    downloadAllTables(filteredLogSummaries, 'allTables');
  }, [filteredLogSummaries]);

  const updateReconcileStatus = useCallback(
    async (row: LogSummaryRow) => {
      const isTargetRow = (candidate: LogSummaryRow) =>
        row.week === candidate.week &&
        row.company === candidate.company &&
        row.network === candidate.network &&
        row.avail === candidate.avail;

      // linearReconciliationStatus only carries rows that have been reconciled, so
      // patching it means dropping the target row and re-adding it iff it's still DNR.
      const withReconciliationStatus = (
        linearReconciliationStatus: LogSummaryRow[],
        targetRow: LogSummaryRow
      ): LogSummaryRow[] => {
        const withoutTarget = linearReconciliationStatus.filter(
          candidate => !isTargetRow(candidate)
        );
        return targetRow.status === 'DNR' ? [...withoutTarget, targetRow] : withoutTarget;
      };

      let previousRow: LogSummaryRow | undefined;
      queryClient.setQueryData<LogSummaries>(
        ADMIN_LOG_SUMMARIES_QUERY_KEY,
        current =>
          current && {
            ...current,
            potentiallyMissing: current.potentiallyMissing.map(missingRow => {
              if (!isTargetRow(missingRow)) return missingRow;
              previousRow = missingRow;
              return row;
            }),
            linearReconciliationStatus: withReconciliationStatus(
              current.linearReconciliationStatus,
              row
            )
          }
      );

      try {
        await updateLinearReconciliation(row);
      } catch (e) {
        const revertedRow = previousRow;
        if (revertedRow) {
          queryClient.setQueryData<LogSummaries>(
            ADMIN_LOG_SUMMARIES_QUERY_KEY,
            current =>
              current && {
                ...current,
                potentiallyMissing: current.potentiallyMissing.map(missingRow =>
                  isTargetRow(missingRow) ? revertedRow : missingRow
                ),
                linearReconciliationStatus: withReconciliationStatus(
                  current.linearReconciliationStatus,
                  revertedRow
                )
              }
          );
        }
        setError({ message: e.message, reportError: e });
      }
    },
    [queryClient, setError]
  );

  const potentiallyMissingHeaders = useMemo(
    () => getPotentiallyMissingHeaders(updateReconcileStatus),
    [updateReconcileStatus]
  );

  return (
    <Page
      title={PAGE_TITLE}
      pageType={PAGE_TITLE}
      className='admin-page'
      minHeight='600px'
      isLoading={isLoading}
      actions={
        <div className='admin-actions'>
          <DatePicker
            range={{ start, end }}
            isOutsideRange={date => date < CUTOFF_DATE_PAST || date > CUTOFF_DATE_FUTURE}
            isDayBlocked={date => !isMonday(parseISO(date))}
            onChange={({ start: newStart, end: newEnd }) => {
              setStart(newStart);
              setEnd(newEnd);
            }}
          />
        </div>
      }
    >
      <div className='admin-page-container'>
        <AdminActionButtons
          workingAction={workingAction}
          onUpdateTrafficClick={() => setShowUpdateTraffic(true)}
          onDownloadTapesClick={downloadLinearTapesReport}
          onClearSheetsClick={clearSheets}
          onClearCompaniesClick={clearCompaniesAndUsers}
          onDownloadAllClick={downloadAll}
        />
        <AlertDialog
          show={showUpdateTraffic}
          onHide={() => setShowUpdateTraffic(false)}
          intent={Intent.SUCCESS}
          title='Update Ad Server Traffic information?'
          body='This will take several minutes to complete.'
          primaryAction={{ onClick: updateTraffic, label: 'Start' }}
          dataTestId='update-traffic-dialog'
        />
        <AdminTables
          logSummaries={filteredLogSummaries}
          potentiallyMissingHeaders={potentiallyMissingHeaders}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </Page>
  );
};
